/**
 * SIGNING IN, at `/sign-in`: the email address, then the emailed code. The step order, the labels
 * and the button words are `faithfully-kept-business/design/home/SignIn.dc.html`'s. Its white card
 * and small right-hand button are not copied: both steps are drawn in the door's style, with the
 * header's Call button, so the phone is one tap away while somebody waits for a code.
 *
 * A MOBILE NUMBER, WHERE THE HOST CAN TEXT (the owner's decision, ops #76, home #11). /api/health
 * says `signInByText`, and only on a clear yes does step 1 take a number: one field labelled for
 * either, a text field with the email keyboard, drawn exactly as the email field is. A number asked
 * with gets the code step in a text's words, mirroring the email's, and always a line saying she
 * can sign in with her email address instead, or call. Where the host cannot text, both steps are
 * the email steps, word for word as they were before.
 *
 * WHAT SHE TYPED IS READ BEFORE IT IS SENT, WHERE THE HOST CAN TEXT (home #11). The field there is a
 * text field, so the browser checks nothing: the page reads the entry by the Worker's own rule
 * (`lib/identifier.ts`), and one that is neither an email address nor a mobile number is not sent,
 * and the page says to check it, as it does for a `400` the Worker still gives. The check is of what
 * she typed alone, never of anything on file. Where the host cannot text, the browser's own email
 * check stands, and a `400` says it did not go through, as it always has.
 *
 * THE PAGE ANSWERS THE SAME WHETHER OR NOT THE ADDRESS IS ON FILE (CLAUDE.md binding rule 3). The
 * Worker answers 202 to every well-shaped request, so step 2 says a code is on its way if that is
 * the email we have, and never that one was sent. One line covers a code that is wrong, used or
 * ended, so the page tells nobody which. Past the ceiling across every sign-in the Worker
 * answers `429 busy` instead, the same for every address and decided before it looks one up, and
 * the page says sign-in is busy, keeps what she typed where it was, and lets her press again. Past
 * thirty codes in an hour for one address or number, the one limit left on asking (the owner's word
 * of 2026-10-20), it answers `429 too_many_codes`, counted the same whether or not the address is
 * on file, and the page says so with the phone, keeping what she typed, and never that a code is on
 * its way.
 *
 * NEVER SILENT (the owner's word of 2026-10-20: "User messages always gives info, never silent").
 * The Worker's 202 names the ask by an id of its own, and step 2 asks what came of that send for a
 * little more than thirty seconds (`lib/codeSent.ts`). Where Microsoft or the phone system did not
 * take the code, "on its way" gives place to why, with the other way in where there is one and the
 * phone, in a status region so it is heard. The owner accepted that a stranger could learn from a
 * failed send that the address or number is a client's; a send that went, however slowly, reads as
 * an ask for an address not on file does, so nothing else is said. Once it was not sent, the rest of
 * the step follows what it says (the fifth client-journey walk's review, findings 1 and 3): the line
 * under the buttons that sends her to check the address or number above, and her spam folder, goes;
 * the button back to step 1 is named as the line names the other way in (`OTHER_WAY_IN`), and so is
 * a code that did not work; and where a new code cannot go either, that button leads. The Code field
 * keeps the cursor and Sign in stays, since a code she asked for earlier still works. A code typed
 * while the guess lock holds is answered
 * `429 codes_locked`, the same on file or not, and the page says codes are paused until when.
 *
 * THE STEPS LIVE IN THIS COMPONENT'S STATE, NEVER IN THE URL, so neither the address nor the code
 * lands in history or a log. Both travel only in the body of a POST. The form is a POST too, so even
 * a submit that somehow skipped this code would not put the address in a query.
 *
 * WHETHER THIS PAGE IS DRAWN IS THE SERVER'S ANSWER. It asks /api/health on arrival, draws only the
 * page colour until the answer comes, and goes back to the door when sign-in is not open, when the
 * question cannot be answered, or when either request says sign-in has closed. After a good code it
 * goes back to `/` in place, and Entry asks /api/me which screen the person sees.
 *
 * BACK TO THE PAGE SHE CAME FOR (the owner's decision of 2026-10-19, ops #76, `docs/seam.md`
 * P107). The door drawn in place of a deeper page sends her here with that page as the one query
 * word, `next`, which stays in this page's own address through both steps, so a reload keeps it.
 * Once she asks for a code, her browser keeps it for the code's thirty minutes, so a sign-in page
 * with none of its own, in another tab of hers, takes it from there (`lib/cameFor.ts`). After a good
 * code it still goes to `/` in place, handing the page she came for to Home in the history, never
 * the address, and forgetting what her browser kept: Home knows whether her household has come
 * down, and sends her on to it, or draws the waiting page first (`Home.tsx`). Only a path on home's
 * own origin among its places is ever handed on; anything else is none, and she lands on Home.
 *
 * One request at a time: the buttons are disabled while one is out, and a submit by the Enter key
 * is refused too. The cursor goes where the next thing to type is: the email field on arrival and on
 * the way back to step 1, the code field on step 2 and after every answer there. The line that
 * answers a press sits in a live region that is always drawn, so a screen reader says it, and it is
 * drawn afresh each time, so the same line said twice is heard twice.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { askForCode, enterCode, getHealth, type CodesLocked, type SendFailure } from '../api.ts';
import { NAVY_BUTTON, TEXT_LINK } from '../components/buttons.ts';
import { COLUMN } from '../components/column.ts';
import { Footer } from '../components/Footer.tsx';
import { Header } from '../components/Header.tsx';
import { Phone } from '../components/Phone.tsx';
import {
  cameForKept,
  forgetCameFor,
  keepCameFor,
  pageCameFor,
  type AfterSignIn,
} from '../lib/cameFor.ts';
import { watchCodeSent } from '../lib/codeSent.ts';
import { forgetCodeAsked } from '../lib/fresh.ts';
import { normaliseIdentifier } from '../lib/identifier.ts';
import { HOME_PATH, NEXT_QUERY } from '../paths.ts';
import {
  codeNotSentBeforePhone,
  codesPausedBeforePhone,
  OTHER_WAY_IN,
  SIGN_IN_BUSY_BEFORE_PHONE,
  TOO_MANY_CODES_BEFORE_PHONE,
} from '../words.ts';

type Step = 'email' | 'code';

/**
 * What she asked with: an email address, or a mobile number, which only a host that can text takes.
 * A string with an `@` in it is an address and anything else a number, as the Worker reads it
 * (`lib/identifier.ts`, the Worker's own rule).
 */
type Channel = 'email' | 'text';

/**
 * Step 1's words: the artboard's, where the host cannot text, and where it can, the same words
 * naming a mobile number and a text beside the email.
 */
const STEP_ONE = {
  emailOnly: {
    eyebrow: '1. Your email',
    about:
      'Use the email address Faithfully Kept has for you. We email you a code. There is no password.',
    label: 'Email address',
  },
  either: {
    eyebrow: '1. Your email or mobile',
    about:
      'Use the email address or mobile number Faithfully Kept has for you. We email or text you a code. There is no password.',
    label: 'Email or mobile number',
  },
} as const;

/**
 * Step 2's words that name the channel: an email's, as the steps have always said them, and a
 * text's, mirroring them word for word but for the channel. Neither says a code was sent. A text's
 * line under the buttons always says she can sign in with her email address instead, or call, the
 * same whether or not the number is on file, while the code is on its way; once it was not sent,
 * the line that says so says it in its place.
 */
const NAMED: Readonly<
  Record<
    Channel,
    {
      readonly eyebrow: string;
      readonly onFile: string;
      readonly different: string;
      readonly wrong: string;
      readonly newCode: string;
      readonly noCodeBeforePhone: string;
    }
  >
> = {
  email: {
    eyebrow: '2. The emailed code',
    onFile: 'is the email we have for you, a code is on its way. Type it here to sign in.',
    different: 'Use a different email',
    wrong: 'That code did not work. Send a new code or use a different email.',
    newCode: 'If that is the email we have for you, a code is on its way.',
    noCodeBeforePhone: 'No code? Check the address above and your spam folder, or call',
  },
  text: {
    eyebrow: '2. The texted code',
    onFile: 'is the mobile number we have for you, a code is on its way. Type it here to sign in.',
    different: 'Use a different number',
    wrong: 'That code did not work. Send a new code or use a different number.',
    newCode: 'If that is the mobile number we have for you, a code is on its way.',
    noCodeBeforePhone:
      'No text? Check the number above, or sign in with your email address, or call',
  },
};

/**
 * The seven lines a press can be answered with: the six it always had, and a code typed while the
 * guess lock holds (`locked`, the owner's word of 2026-10-20, never silent).
 */
type Said = 'wrong' | 'failed' | 'new_code' | 'busy' | 'too_many_codes' | 'unreadable' | 'locked';

/**
 * Where the host can text: what she typed is neither an email address nor a mobile number, by the
 * Worker's own rule, read before it is sent or answered `400` by the Worker.
 */
const CHECK_ENTRY = 'Please check the email address or mobile number.';

/**
 * A line, and the count of lines said so far, which draws each one as a new node; for the guess
 * lock, until when, as the Worker said it.
 */
interface Line {
  readonly said: Said;
  readonly count: number;
  readonly until?: CodesLocked['locked'];
}

/** A code is six digits. Spaces a person types between them are taken out first. */
const SIX_DIGITS = /^\d{6}$/;

const EYEBROW = 'font-mono text-small tracking-widest text-text-emphasis uppercase';
const HEADING = 'mt-2 font-serif text-h2 font-semibold text-pretty text-text-body';
const LABEL = 'mt-7 block text-body font-semibold';
/** The artboard's underlined field: a 2px rule in the strong border colour, at least 48px tall. */
const FIELD =
  'mt-1 block min-h-tap w-full border-b-2 border-border-strong bg-transparent py-2.5 text-body ' +
  'text-text-body';
const SUBMIT = `mt-7 ${NAVY_BUTTON} disabled:cursor-wait`;
const LINK_BUTTON = `text-body ${TEXT_LINK} disabled:cursor-wait`;

function Words({
  said,
  channel,
  until,
  otherWay,
}: {
  said: Said;
  channel: Channel;
  until?: CodesLocked['locked'];
  otherWay: boolean;
}) {
  switch (said) {
    case 'locked':
      return (
        <>
          {codesPausedBeforePhone(until ?? null)} <Phone after="." />
        </>
      );
    case 'wrong':
      return <>{otherWay ? OTHER_WAY_IN[channel].wrong : NAMED[channel].wrong}</>;
    case 'new_code':
      return <>{NAMED[channel].newCode}</>;
    case 'unreadable':
      return <>{CHECK_ENTRY}</>;
    case 'failed':
      return (
        <>
          That did not go through. Please try again, or call <Phone after="." />
        </>
      );
    case 'busy':
      return (
        <>
          {SIGN_IN_BUSY_BEFORE_PHONE} <Phone after="." />
        </>
      );
    case 'too_many_codes':
      return (
        <>
          {TOO_MANY_CODES_BEFORE_PHONE} <Phone after="." />
        </>
      );
  }
}

/**
 * The live region, drawn empty until a press is answered. `otherWay`: the code step's button back
 * names the other way in, so a code that did not work names it too.
 */
function Answer({
  line,
  channel,
  otherWay = false,
}: {
  line: Line | null;
  channel: Channel;
  otherWay?: boolean;
}) {
  return (
    <div aria-live="polite">
      {line ? (
        <p key={line.count} className="mt-3 text-body text-text-body">
          <Words said={line.said} channel={channel} until={line.until} otherWay={otherWay} />
        </p>
      ) : null}
    </div>
  );
}

export function SignIn() {
  const navigate = useNavigate();
  const [query] = useSearchParams();
  /**
   * The page she came for: this page's own, else the one her browser kept as she asked for a code
   * in another tab, read once as the page is drawn; null for none.
   */
  const [cameFor] = useState(() => pageCameFor(query.get(NEXT_QUERY)) ?? cameForKept());
  const [open, setOpen] = useState<boolean | null>(null);
  /** Whether this host can text a code, so step 1 takes a mobile number as well as an address. */
  const [byText, setByText] = useState(false);
  const [step, setStep] = useState<Step>('email');
  /** The address or number she typed, trimmed once she asks for a code with it. */
  const [identifier, setIdentifier] = useState('');
  /** What the code step names: the channel of the address or number she asked with. */
  const [channel, setChannel] = useState<Channel>('email');
  const [code, setCode] = useState('');
  const [line, setLine] = useState<Line | null>(null);
  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState<{ readonly on: Step } | null>(null);
  /**
   * Why the code last asked for was not sent, as what came of its send said (the owner's word of
   * 2026-10-20, never silent), said in place of "on its way"; null while nothing says so.
   */
  const [notSent, setNotSent] = useState<SendFailure | null>(null);

  /** Read synchronously, so a second press in the same moment as the first is refused. */
  const out = useRef(false);
  const mounted = useRef(false);
  const lines = useRef(0);
  const emailField = useRef<HTMLInputElement>(null);
  const codeField = useRef<HTMLInputElement>(null);
  /** Stops asking what came of the last code's send, while that is being asked. */
  const watching = useRef<(() => void) | null>(null);

  const stopWatching = () => {
    watching.current?.();
    watching.current = null;
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      stopWatching();
    };
  }, []);

  useEffect(() => {
    let live = true;
    void getHealth().then((health) => {
      if (!live) return;
      setOpen(health.signIn);
      setByText(health.signInByText);
      if (health.signIn) setFocus({ on: 'email' });
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (focus) (focus.on === 'email' ? emailField : codeField).current?.focus();
  }, [focus]);

  const say = (said: Said, until?: CodesLocked['locked']) => {
    lines.current += 1;
    setLine({ said, count: lines.current, until });
  };

  /**
   * A code was asked for, and the Worker named the ask: what came of its send is asked about for a
   * while (`lib/codeSent.ts`), and a send that failed is said in place of "on its way", the line
   * under the field that said a new code is on its way going with it. Any earlier asking stops, and
   * so does what an earlier send said. A Worker that names no ask is asked nothing.
   */
  const watch = (ask: string | null) => {
    stopWatching();
    setNotSent(null);
    if (ask === null) return;
    watching.current = watchCodeSent(ask, (why) => {
      watching.current = null;
      if (!mounted.current) return;
      setNotSent(why);
      setLine((current) => (current?.said === 'new_code' ? null : current));
    });
  };

  const begin = () => {
    out.current = true;
    setBusy(true);
    setLine(null);
  };

  const end = () => {
    out.current = false;
    setBusy(false);
  };

  const toTheDoor = () => void navigate(HOME_PATH, { replace: true });

  /** Signed in: to `/` in place, handing Home the page she came for in the history. */
  const inside = () => {
    const after: AfterSignIn | null = cameFor === null ? null : { after: cameFor };
    void navigate(HOME_PATH, { replace: true, state: after });
  };

  /**
   * The line for what the Worker said of a code asked for: a `400`, what she typed not being read,
   * says to check it where the host can text, and where it cannot, that it did not go through, as it
   * always has.
   */
  const unanswered = (answer: 'failed' | 'busy' | 'too_many_codes' | 'unreadable'): Said =>
    answer === 'unreadable' && !byText ? 'failed' : answer;

  async function next(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (out.current) return;
    const asked = identifier.trim();
    // Where the host can text, the field is a text field, so what she typed is read here by the
    // Worker's own rule, and an entry it would refuse is not sent. Where it cannot, the browser has
    // checked the address already, and nothing is read here.
    const read = byText ? normaliseIdentifier(asked) : null;
    if (byText && read === null) {
      say('unreadable');
      setFocus({ on: 'email' });
      return;
    }
    begin();
    const { answer, ask } = await askForCode(asked);
    if (!mounted.current) return;
    end();
    if (answer === 'closed') return toTheDoor();
    if (answer !== 'asked') {
      // Nothing was asked for, so she stays here, with the address as she typed it, to press again.
      say(unanswered(answer));
      setFocus({ on: 'email' });
      return;
    }
    // A code is on its way if the address is on file: her browser keeps the page she came for for
    // the code's thirty minutes, so a sign-in finished in another tab of hers lands there too.
    if (cameFor !== null) keepCameFor(cameFor);
    setIdentifier(asked);
    // Only a host that can text takes a number at all; where it cannot, the field is an email
    // field, and the code step is the email's, as it always has been.
    setChannel(read?.channel === 'sms' ? 'text' : 'email');
    setCode('');
    setStep('code');
    setFocus({ on: 'code' });
    watch(ask);
  }

  async function sendAgain() {
    if (out.current) return;
    begin();
    const { answer, ask } = await askForCode(identifier);
    if (!mounted.current) return;
    end();
    if (answer === 'closed') return toTheDoor();
    if (answer === 'asked') {
      // A new code, good for thirty minutes more: so is the page she came for.
      if (cameFor !== null) keepCameFor(cameFor);
      say('new_code');
      setCode('');
      watch(ask);
    } else {
      say(unanswered(answer));
    }
    setFocus({ on: 'code' });
  }

  async function signIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (out.current) return;
    const typed = code.replace(/\s/g, '');
    if (!SIX_DIGITS.test(typed)) {
      say('wrong');
      setFocus({ on: 'code' });
      return;
    }
    begin();
    const answer = await enterCode(identifier, typed);
    // A new session, with codes of its own: a code a fresh-code step asked for before it is no use
    // to it, so a step drawn next asks for one (`lib/fresh.ts`, round four, slice M3). And the page
    // she came for goes to Home with her now, so her browser forgets it (`lib/cameFor.ts`).
    if (answer === 'signed_in') {
      forgetCodeAsked();
      forgetCameFor();
      stopWatching();
    }
    if (!mounted.current) return;
    end();
    if (answer === 'signed_in') return inside();
    if (answer === 'closed') return toTheDoor();
    if (typeof answer === 'object') {
      // The guess lock refused the try before it was compared: it says so, and until when, and
      // what she typed stays for once it lifts.
      say('locked', answer.locked);
    } else if (answer === 'not_accepted') {
      say('wrong');
      setCode('');
    } else {
      say('failed');
    }
    setFocus({ on: 'code' });
  }

  function differentOne() {
    if (out.current) return;
    stopWatching();
    setNotSent(null);
    setLine(null);
    setCode('');
    setStep('email');
    setFocus({ on: 'email' });
  }

  if (open === null) return <div className="min-h-dvh bg-surface-page" />;
  if (!open) return <Navigate to={HOME_PATH} replace />;

  const stepOne = byText ? STEP_ONE.either : STEP_ONE.emailOnly;
  const named = NAMED[channel];
  /** The other way in this page has: her email for a text, and her mobile where texts can go. */
  const otherWayIn = channel === 'text' || byText;
  /** The code was not sent, and the line that says so offers the other way in. */
  const offersOther = notSent !== null && otherWayIn;
  /**
   * A new code cannot go either: the number asked not to get our texts, or texts or email cannot
   * reach it. The way back to step 1 leads then, as the line that says so offers no try again.
   */
  const backFirst = notSent === 'stopped' || notSent === 'not_enabled';
  const sendNew = (
    <button
      key="new"
      type="button"
      disabled={busy}
      onClick={() => void sendAgain()}
      className={LINK_BUTTON}
    >
      Send a new code
    </button>
  );
  const goBack = (
    <button key="back" type="button" disabled={busy} onClick={differentOne} className={LINK_BUTTON}>
      {offersOther ? OTHER_WAY_IN[channel].button : named.different}
    </button>
  );

  return (
    <div className="flex min-h-dvh flex-col bg-surface-page">
      <Header />

      <main className={`${COLUMN} flex-1 px-5 py-8`}>
        {step === 'email' ? (
          <form key="email" method="post" onSubmit={(event) => void next(event)}>
            <p className={EYEBROW}>{stepOne.eyebrow}</p>
            <h1 className={HEADING}>Sign in</h1>
            <p id="sign-in-email-about" className="mt-4 text-body text-pretty">
              {stepOne.about}
            </p>

            <label htmlFor="sign-in-email" className={LABEL}>
              {stepOne.label}
            </label>
            <input
              ref={emailField}
              id="sign-in-email"
              // Where the host can text, a text field, so a number goes through, with the email
              // keyboard, which has the @ and the digits both; where it cannot, the email field.
              type={byText ? 'text' : 'email'}
              inputMode={byText ? 'email' : undefined}
              name={byText ? 'username' : 'email'}
              autoComplete={byText ? 'username' : 'email'}
              required
              maxLength={254}
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              aria-describedby="sign-in-email-about"
              className={FIELD}
            />
            <Answer line={line} channel={channel} />

            <button type="submit" disabled={busy} className={SUBMIT}>
              Next
            </button>
          </form>
        ) : (
          <form key="code" method="post" onSubmit={(event) => void signIn(event)}>
            <p className={EYEBROW}>{named.eyebrow}</p>
            <h1 className={HEADING}>Enter code</h1>
            {/* A status region, so a send that failed is heard as it takes the place of "on its
                way" (never silent, the owner's word of 2026-10-20). */}
            <p
              id="sign-in-code-about"
              role="status"
              className="mt-4 text-body text-pretty wrap-break-word"
            >
              {notSent === null ? (
                <>
                  If <span className="inline-block max-w-full wrap-anywhere">{identifier}</span>
                  {` ${named.onFile}`}
                </>
              ) : (
                <>
                  {codeNotSentBeforePhone(notSent, channel, otherWayIn)} <Phone after="." />
                </>
              )}
            </p>

            <label htmlFor="sign-in-code" className={LABEL}>
              Code
            </label>
            <input
              ref={codeField}
              id="sign-in-code"
              type="text"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              aria-describedby="sign-in-code-about"
              className={FIELD}
            />
            <Answer line={line} channel={channel} otherWay={offersOther} />

            <div className="mt-3 flex flex-col items-start">
              {backFirst ? [goBack, sendNew] : [sendNew, goBack]}
            </div>
            {/* Where to look for a code on its way: gone once it was not sent, since the address or
                number it names above is no longer there and the code it points to never went (the
                fifth client-journey walk's review, findings 1 and 3). The line that says so names
                the phone in its place. */}
            {notSent === null ? (
              <p className="mt-3 text-small text-text-muted">
                {named.noCodeBeforePhone} <Phone after="." />
              </p>
            ) : null}

            <button type="submit" disabled={busy} className={SUBMIT}>
              Sign in
            </button>
          </form>
        )}
      </main>

      <Footer />
    </div>
  );
}
